import { AgGrid } from "@jsq/ds/core";
import { DefaultColumnTypes } from "#utils/agGrid.constants";
import { DEF_FILTER_DEBOUNCE_MS } from "../../constants";
import Money from "../../Money";
import { AmountCell } from "./components/AmountCell";
import { EntityCell } from "./components/EntityCell";
import { type CashTransactionRowItem } from "./types";
import { type BankAccountFilterOption } from "./useCashTransactionFilterValuesStub";

/** Options for `getColumnDefinitions` — callbacks + filter-value lists the column defs wire in. */
export type GetColumnDefinitionsOptions = {
  onEntityClick?: (transaction: CashTransactionRowItem) => void;
  /** Explicit `agSetColumnFilter` values for the Entity column — see `useCashTransactionFilterValuesStub`. */
  entityNameFilterValues?: string[];
  /** Explicit `agSetColumnFilter` values (+ display labels) for the Bank account column. */
  bankAccountFilterOptions?: BankAccountFilterOption[];
};

/**
 * List of the supported columns in the CashTransactionsTable
 * (ordered left to right as specified in the LLD).
 */
export enum SupportedColumns {
  PostDate = "postDate",
  EntityName = "entityName",
  BankAccountLast4 = "bankAccountLast4",
  Description = "description",
  Amount = "amount",
  Balance = "balance",
}

/**
 * The mapper provides the corresponding column name displayed in the table.
 */
export const columnNames = new Map<SupportedColumns, string>([
  [SupportedColumns.PostDate, "Date"],
  [SupportedColumns.EntityName, "Entity"],
  [SupportedColumns.BankAccountLast4, "Bank account"],
  [SupportedColumns.Description, "Description"],
  [SupportedColumns.Amount, "Amount"],
  [SupportedColumns.Balance, "Balance"],
]);

/**
 * Creates AG Grid column definitions for the CashTransactionsTable.
 *
 * STATIC column set — the transactions table has no group-by, so a single
 * fixed set is returned. Column filters are AG Grid's native filters; Entity /
 * Bank account use `agSetColumnFilter` with `filterParams.values` supplied
 * explicitly (via `options`) rather than left to AG Grid's own inference —
 * under a server-side row model, that inference only sees the currently-loaded
 * page, which under-populates the picker. No column is user-sortable — the
 * datasource's own default ordering is what's shown.
 */
export const getColumnDefinitions = (
  options: GetColumnDefinitionsOptions = {},
): AgGrid.ColDef<CashTransactionRowItem>[] => {
  // Keyed by bankAccountId so the set filter's own display formatter (below)
  // can label it "{bankName} ...{last4}" without a full row.
  const bankAccountLabelById = new Map(
    (options.bankAccountFilterOptions ?? []).map((option: BankAccountFilterOption) => [
      option.value,
      option.label,
    ]),
  );

  return [
    {
      field: SupportedColumns.PostDate,
      headerName: columnNames.get(SupportedColumns.PostDate),
      filter: "agDateColumnFilter",
      // Restricted to inRange — buildCashTxnFilters maps dateFrom/dateTo as an
      // inclusive range, which only matches inRange's semantics (lessThan/
      // greaterThan/equals put a single boundary value in dateFrom instead).
      filterParams: { filterOptions: ["inRange"], maxNumConditions: 1 },
    },
    {
      field: SupportedColumns.EntityName,
      headerName: columnNames.get(SupportedColumns.EntityName),
      filter: "agSetColumnFilter",
      filterParams: { values: options.entityNameFilterValues ?? [] },
      cellRenderer: EntityCell,
      cellRendererParams: { onEntityClick: options.onEntityClick },
    },
    {
      field: SupportedColumns.BankAccountLast4,
      headerName: columnNames.get(SupportedColumns.BankAccountLast4),
      filter: "agSetColumnFilter",
      filterParams: {
        values: (options.bankAccountFilterOptions ?? []).map(
          (option: BankAccountFilterOption) => option.value,
        ),
        // The set filter's own panel has no `data` to derive bankName from — label
        // each bankAccountId from the same option list `values` was built from.
        valueFormatter: ({ value }: { value: string }) =>
          bankAccountLabelById.get(value) ?? value,
      },
      // Show "{bankName} ...{last4}" (e.g. "JP Morgan Chase ...2939").
      valueFormatter: ({ data }: { data?: CashTransactionRowItem }) =>
        data?.bankAccountLast4 ? `${data.bankName} ...${data.bankAccountLast4}` : "",
    },
    {
      field: SupportedColumns.Description,
      headerName: columnNames.get(SupportedColumns.Description),
      minWidth: 200,
      filter: "agTextColumnFilter",
      // Restricted to `contains` — buildCashTxnFilters maps the description filter
      // to a substring match; other operators (equals/startsWith/endsWith) would
      // imply semantics the (stub, and eventual server-side) query doesn't honor.
      filterParams: {
        filterOptions: ["contains"],
        maxNumConditions: 1,
        debounceMs: DEF_FILTER_DEBOUNCE_MS,
      },
    },
    {
      field: SupportedColumns.Amount,
      headerName: columnNames.get(SupportedColumns.Amount),
      type: DefaultColumnTypes.numericColumn,
      headerClass: "ag-left-aligned-header",
      filter: "agNumberColumnFilter",
      // inRange / greaterThan / lessThan only — buildCashTxnFilters maps each of
      // these to amountMin/amountMax; other operators (equals, notEqual, ...)
      // aren't modeled there, so they're excluded from the picker.
      filterParams: {
        filterOptions: ["inRange", "greaterThan", "lessThan"],
        maxNumConditions: 1,
        debounceMs: DEF_FILTER_DEBOUNCE_MS,
      },
      // Signed + colored (+green / −red) per the design.
      cellRenderer: AmountCell,
    },
    {
      field: SupportedColumns.Balance,
      headerName: columnNames.get(SupportedColumns.Balance),
      type: DefaultColumnTypes.numericColumn,
      // Last column — not resizable (precedent: CashAccountsTable's Delta column).
      resizable: false,
      cellRenderer: Money,
      cellRendererParams: ({ data }: { data?: CashTransactionRowItem }) => ({
        money: data?.balance,
        rounded: false,
      }),
    },
  ];
};

/** Row count per server-side page; drives cacheBlockSize + pagination page size. */
export const ROW_COUNT_PER_PAGE = 10;

/** Column defaults applied to every column in the grid. */
export const defaultColDef: AgGrid.ColDef<CashTransactionRowItem> = {
  editable: false,
  flex: 1,
  sortable: false,
  menuTabs: ["filterMenuTab"],
};

/** Grid-level options shared across the table's usages. */
export const gridOptions: AgGrid.GridOptions<CashTransactionRowItem> = {
  enableCellTextSelection: true, // Allows text to be copied from cells
  pagination: true,
  paginationPageSize: ROW_COUNT_PER_PAGE,
  paginationPageSizeSelector: false,
  rowHeight: 50,
  suppressCellFocus: true,
  suppressContextMenu: true,
  // Filter icon is always visible instead of only on hover (precedent: CashAccountsTable).
  suppressMenuHide: true,
};
